import { useMemo } from 'react';
import { forwardRef, useMultiStyleConfig, useRadioGroup, UseRadioGroupProps } from '@chakra-ui/react';
import Box, { BoxProps } from '../Box/Box';
import { SegmentedControlContext, SegmentedControlContextType } from './SegmentedControl.context';

export type SegmentedControlProps = Omit<BoxProps, 'onChange'> & UseRadioGroupProps;

/**
 * This is a custom radio button group, based on: https://chakra-ui.com/docs/components/radio/usage#custom-radio-buttons.
 */
const SegmentedControl = forwardRef<SegmentedControlProps, 'div'>((props, ref) => {
  const { defaultValue, isDisabled, name, onChange, value, ...rest } = props;

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue,
    isDisabled,
    name,
    onChange,
    value,
  });

  const ctx = useMemo<SegmentedControlContextType>(
    () => ({
      getRadioProps,
      isGroupDisabled: isDisabled,
    }),
    [getRadioProps, isDisabled],
  );

  const group = getRootProps();

  const theme = useMultiStyleConfig('SegmentedControl');

  return (
    <SegmentedControlContext value={ctx}>
      <Box ref={ref} __css={theme.container} {...group} {...rest} />
    </SegmentedControlContext>
  );
});

export default SegmentedControl;
